import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import setRequistionCartPresent from "@tKgpco/Handlers/RequistionCart/Handlers/RequistionCartHandler";
import setShowRequisitionCartDisclaimer from "@tKgpco/Handlers/RequistionCart/Handlers/showRequisitionCartDisclaimer";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { COLORS } from "@tKgpco/theme/design";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    cartState: getCurrentCartState(state).value,
    userName: state.context.session.userName,
    isShowDisclaimerForRequistionCartOpen: state.KGPState.RequistionCartStore.showDisclaimerForRequistionCart,
});

const mapDispatchToProps = {
    loadCurrentCart,
    setRequistionCartPresent,
    setShowRequisitionCartDisclaimer,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    cartText?: InjectableCss;
    cartQty?: InjectableCss;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "text.main",
        css: css`
            height: 30px;
            // padding: 0 4px 0 10px;
            padding: 0;
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 60px;
            svg {
                stroke-width: 1.5;
            }
            @media print {
                display: none !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-right: 6px;
                            padding-left: 0;
                        `,
                        css`
                            padding-right: 6px;
                            padding-left: 0;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    cartText: {
        css: css`
            font-weight: 400;
            text-transform: capitalize;
            font-size: 11px;
            color: ${getColor("text.main")};
        `,
    },
    cartQty: {
        css: css`
            background: ${COLORS.primary.base};
            width: auto;
            min-width: 19px;
            height: 17px;
            padding: 1px;
            display: block;
            border-radius: 10px;
            color: #fff;
            text-align: center;
            font-size: 11px;
            position: absolute;
            right: 15px;
            top: -7px;
            font-weight: 700;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    cartState,
    userName,
    setRequistionCartPresent,
    isShowDisclaimerForRequistionCartOpen,
    setShowRequisitionCartDisclaimer,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
        }
    }, [shouldLoadCart]);

    useEffect(() => {
        if (cartState && userName && cartState?.initiatedByUserName !== userName && cartState.isAwaitingApproval) {
            setRequistionCartPresent({ isRequistionCartPresent: true });
        } else {
            setRequistionCartPresent({ isRequistionCartPresent: false });
        }
    }, [cartState, userName]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    return (
        <>
            <Link
                {...routerLink}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Hidden below="lg" as="span" {...styles.cartText}>
                    {showLabel && `${translate("My Cart")} `}
                </Hidden>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
                <StyledWrapper data-test-selector="cartLinkQuantity" {...styles.cartQty}>
                    {retainedTotalCountToDisplay}
                </StyledWrapper>
            </Link>
            <Modal
                headline={translate("Approval Cart Active")}
                isOpen={isShowDisclaimerForRequistionCartOpen}
                handleClose={() => setShowRequisitionCartDisclaimer({ showDisclaimerForRequistionCart: false })}
                sizeVariant="medium"
            >
                {siteMessage("RequisitionCartDisclaimer_Message")}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
